import React, { useState, useEffect } from 'react';
import {  useNavigate, Link } from 'react-router-dom';
import './PredictionPage.css';
import AdUnit from './AdUnit';
import AdRotator from '../components/AdRotator';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const GCP_PRD_API_URL = process.env.PRD_API_URL;


// Event descriptions mapping
const EVENT_DESCRIPTIONS = {
  "edsas01": "Attended grade 1 in school",
  "edsas08": "Attend grade 8 in school",
  "edsas12": "Attend grade 12 in school",
  "edhafdu": "Attend full-time, degree unknown",
  "secvavc": "Suffer from a crime or incident",
  "edhefdu": "Enroll in full-time education",
  "edhemsd": "Enroll with masters degree",
  "edhebsa": "Enroll with a BA or BSc",
  "edhebeg": "Enroll with a BE",
  "edhebba": "Enroll with a Bachelor of Business",
  "edhephd": "Enroll with a PhD",
  "edheedu": "Enroll with a degree",
  "edhepro": "Enroll with a Professional degree",
  "edhgmsd": "Graduate with Masters degree",
  "edhgbsa": "Graduate with a BA or BSc",
  "edhgbeg": "Graduate with a Bachelor Eng",
  "edhgbba": "Graduate with a Bachelor of Business",
  "edhgphd": "Graduate with a doctoral degree, phd",
  "edhgedu": "Graduate with a degree",
  "edhgpro": "Graduate with a professional degree",
  "crrsjnj": "Start a new job",
  "crrprjb": "Promotion in job",
  "crrlqjb": "Change job",
  "crrrtjb": "Retire from job",
  "prsbapr": "Buy an apartment or a house",
  "prssafp": "Buy an apartment or a house with partner",
  "socpwfd": "Go on a date with someone",
  "socpmrr": "Get married",
  "socpdvd": "Get a divorce",
  "socbprg": "Get pregnant",
  "socbbpr": "Your partner get pregnant",
  "socnb01": "A New born first child",
  "hthchgn": "Might have a chronic health condition",
  "hthchiv": "Beware of STD (HIV/HPV)",
  "hthcstd": "Get an STD",
  "hthcanm": "Get anemia",
  "hthcazm": "Get asthma",
  "hthccnr": "Get cancer",
  "hthcdbt": "Get diabetes",
  "hthcepl": "Get epilepsy",
  "hthchrt": "Get heart condition",
  "prslrfc": "Relocate to another country",
  "prslctr": "Relocate within Country",
  "prslstt": "Relocate within State",
  "prslctn": "Relocate within the county"
};

// Event vocabulary organized by category
const EVENT_VOCABULARY = [
  {
    category: "EDUCATION",
    events: [
      { code: "edsas08", label: "Finished Middle School" },
      { code: "edsas12", label: "Finished High School" },
      { code: "edhebba", label: "Enroll in BBA" },
      { code: "edhgbba", label: "Graduated BBA" },
      { code: "edhebeg", label: "Enrolled in B.E." },
      { code: "edhgbeg", label: "Graduated B.E." },
      { code: "edhebsa", label: "Enroll in BA/BSc" },
      { code: "edhgbsa", label: "Graduate BA/BSc" },
      { code: "edhemsd", label: "Enroll in Masters" },
      { code: "edhgmsd", label: "Graduate Masters" },
      { code: "edhephd", label: "Enroll in PhD" },
      { code: "edhgphd", label: "Graduate PhD" },
      { code: "edhepro", label: "Enroll Professional Degree" },
      { code: "edhgpro", label: "Graduate Professional Degree" },
      { code: "edheedu", label: "Enroll in Degree" },
      { code: "edhgedu", label: "Graduate with Degree" }
    ]
  },
  {
    category: "CAREER",
    events: [
      { code: "crrsjnj", label: "Start a new job" },
      { code: "crrprjb", label: "Got promoted on the job" },
      { code: "crrlqjb", label: "Change or quit job" },
      { code: "crrrtjb", label: "Retired from job" }
    ]
  },
  {
    category: "FAMILY",
    events: [
      { code: "socpwfd", label: "Had on a date with someone" },
      { code: "socpmrr", label: "Got married" },
      { code: "socbprg", label: "Got pregnant" },
      { code: "socnb01", label: "Gave Birth to a new born" },
      { code: "socbbpr", label: "Partner got pregnant" },
      { code: "socpdvd", label: "Got a divorce" }
    ]
  },
  {
    category: "HEALTH",
    events: [
      { code: "hthchiv", label: "Diagnosed with HIV/aids" },
      { code: "hthcstd", label: "Diagnosed with an STD" },
      { code: "hthcanm", label: "Diagnosed with anemia" },
      { code: "hthcazm", label: "Diagnosed with asthma" },
      { code: "hthccnr", label: "Diagnosed with cancer" },
      { code: "hthcdbt", label: "Diagnosed with diabetes" },
      { code: "hthcepl", label: "Diagnosed with epilepsy" },
      { code: "hthchrt", label: "Diagnosed with a heart condition" }
    ]
  },
  {
    category: "LOCATION",
    events: [
      { code: "prsbapr", label: "Bought apartment/house" },
      { code: "prssafp", label: "Bought apartment/house with partner" },
      { code: "prslctn", label: "Relocated within my county" },
      { code: "prslstt", label: "Relocated within my State" },
      { code: "prslctr", label: "Relocated within the country" },
      { code: "prslrfc", label: "Relocated to another country" }
    ]
  }
];

const AdSensePlaceholder = ({ position }) => (
  <div className="adsense-placeholder">
    <div className="ad-content">
      <span className="ad-label"></span>
      <p className="ad-text">{/*AdSense Ad #position*/}
        <AdUnit adSlot="1700647405" /> {/* Use your actual ad slot ID */}
      </p>
    </div>
  </div>
);


const PredictionPage = ({ user, token, onUpgradeClick }) => {
  const navigate = useNavigate();

  const [selectedPersona, setSelectedPersona] = useState('self');
  const [eventSequence, setEventSequence] = useState(['', '', '', '', '', '']);
  const [prediction, setPrediction] = useState(null);
  const [predictionText, setPredictionText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [personas, setPersonas] = useState([]);

  const [dateOfBirth, setDateOfBirth] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [gender, setGender] = useState('');
  const [location, setLocation] = useState('');

  const isFree = user.tier === 'free';
  const maxSequences = user.tier === 'free' ? 4 : user.tier === 'paid' ? 6 : 8;
  const remainingPredictions = getRemainingPredictions();

  useEffect(() => {
    if (user.profile) {
      setDateOfBirth(user.profile.dateOfBirth || '');
      setEthnicity(user.profile.ethnicity || '');
      setGender(user.profile.gender || '');
      setLocation(user.profile.location || '');
    }
  }, [user]);

  useEffect(() => {
    fetchPersonas();
  }, []);

  function getRemainingPredictions() {
    const limits = {
      free: 5,
      paid: 15,
      premium: 25
    };
    return limits[user.tier] - (user.dailyPredictionCount || 0);
  }

  const fetchPersonas = async () => {
    try {
      const response = await fetch(`${API_URL}/personas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPersonas(data);
      }
    } catch (err) {
      console.error('Error fetching personas:', err);
    }
  };

  const handleEventChange = (index, value) => {
    const newSequence = [...eventSequence];
    newSequence[index] = value;
    setEventSequence(newSequence);
  };

  const handlePredict = async () => {
    setError('');
    setPrediction(null);

    const filledEvents = eventSequence.filter(e => e !== '').slice(0, maxSequences);
    if (filledEvents.length < 2) {
      setError('Please select at least 2 events for prediction');
      return;
    }

    if (remainingPredictions <= 0) {
      setError('Daily prediction limit reached. Please upgrade or try again tomorrow!');
      onUpgradeClick();
      return;
    }

    setLoading(true);

    try {
      // Call the external prediction API directly
//      const predictionApiUrl = '${GCP_PRD_API_URL}/predict';
//      await fetch(predictionApiUrl, {
      const predictionResponse = await fetch('https://prediction-api-302792922407.us-central1.run.app/predict', {
//      const predictionResponse = await fetch(`${GCP_PRD_API_URL}/predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          event_sequence: filledEvents
        })
      });

      if (!predictionResponse.ok) {
        throw new Error('Prediction API failed');
      }

      const predictionData = await predictionResponse.json();

      if (predictionData.status === 'success') {
        // Now update our backend to track the prediction count
        await fetch(`${API_URL}/predict`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            personaId: selectedPersona,
            sequenceEvents: filledEvents
          })
        });

        setPrediction(predictionData.prediction_summary);

        const filledEventsData = filledEvents.map((item, index) => (getEventDescription(item) ))

        const forecastedData = predictionData.prediction_summary.top_events.map((item, index) => (
                  getEventDescription(item.event) +" "+(item.probability * 100).toFixed(2)+"%" ) )


        ////////////////add the explanation from the explantion api
        if (user.profile.gender && user.profile.dateOfBirth && user.profile.ethnicity && user.profile.location){
          const explanation_body = JSON.stringify({
                name: user.email.split("@",1)[0],//selectedPersona,
                gender: user.profile.gender,//"Male",
                age: getAge(user.profile.dateOfBirth),
                ethnicity: user.profile.ethnicity,
                language: "English",
                numberOfTokens: 1500,
                pastEvents: filledEventsData,
                forecastedEvents: forecastedData
                });

          
          console.log(explanation_body);
          const predictionExplanation = await fetch('https://mello-ex-302792922407.us-central1.run.app/analyze', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: explanation_body
          });

          if (!predictionExplanation.ok) {
            throw new Error('Explanation API failed');
          }

          const predictionDataExp = await predictionExplanation.json();
          console.log(predictionDataExp);
          setPredictionText(predictionDataExp.analysis);
        }
        ///////////////////////////////////////////////////////////
      } else {
        throw new Error(predictionData.error || 'Prediction failed');
      }
    } catch (err) {
      setError('Network error. Please ensure the prediction API is running ');
      //console.error('Prediction error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAge = birthDate => Math.floor((new Date() - new Date(birthDate).getTime()) / 3.15576e+10)

  const getEventDescription = (eventCode) => {
    return EVENT_DESCRIPTIONS[eventCode] || eventCode;
  };

  return (
    <div className="prediction-page">
      <div className="cosmic-bg"></div>
      
      <div className="page-header">
        <span className='label-icon'>✨ ✨ ✨<h1 className="page-title">Your Future Awaits</h1></span>
        <p className="page-subtitle">Discover what the universe has in store for you</p>
      </div>

      <div className="headerAd">
    
        <AdRotator floating={true} placement="sidebar" />
      </div>

      <div className="content-grid">
        {/* Left Column - Main Content */}
        <div className="main-content">
          {/* Persona Selector */}
          <div className="card persona-selector-card">
            <label className="input-label">
              <span className="label-icon">👤</span>
              {/*Choose Persona */}Prediction for
            </label>
            <select 
              className="persona-select"
              value={selectedPersona}
              onChange={(e) => setSelectedPersona(e.target.value)}
              disabled={true}
            >
              <option value="self">Myself</option>
              {/*personas.map(persona => (
                <option key={persona.id} value={persona.id}>
                  {persona.name}
                </option>
              ))*/}
            </select>
            <div className="prediction-counter">
              <span className="counter-icon">🔮</span>
              <span>{remainingPredictions} prediction(s) remaining today</span>
            </div>
          </div>

          {/* Event Sequence Builder */}
          <div className="card sequence-card">
            <h2 className="card-title">
              <span className="title-icon">📝</span>
              Build Your Event Sequence
            </h2>
            <p className="card-subtitle">Select life events in chronological order (oldest to most recent)</p>

            <div className="event-slots">
              {eventSequence.slice(0, maxSequences).map((event, index) => (
                <div key={index} className="event-slot">
                  <div className="slot-number">{index + 1}</div>
                  <select
                    className="event-select"
                    value={event}
                    onChange={(e) => handleEventChange(index, e.target.value)}
                    disabled={isFree && index >= 4}
                  >
                    <option value="">-- Select an event --</option>
                    {EVENT_VOCABULARY.map(category => (
                      <optgroup key={category.category} label={category.category}>
                        {category.events.map(evt => (
                          <option key={evt.code} value={evt.code}>
                            {evt.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {isFree && index >= 4 && (
                    <div className="locked-overlay" onClick={onUpgradeClick}>
                      <span className="lock-icon">🔒</span>
                      <span>Upgrade to unlock</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button 
              className="predict-btn"
              onClick={handlePredict}
              disabled={loading || remainingPredictions <= 0}
            >
              {loading ? (
                //(user.profile.gender && user.profile.dateOfBirth && user.profile.ethnicity && user.profile.location)
                <>
                  <span className="spinner-small"></span>
                  <span>Consulting the cosmos...</span>
                </>
              ) : (
                <>
                  <span>✨ Get My Prediction ✨</span>
                </>
              )}
            </button>

            {isFree && (
              <button className="upgrade-hint-btn" onClick={onUpgradeClick}>
                <span className="hint-icon">💡</span>
                <span>Upgrade to add up to 6 events for more accurate predictions</span>
              </button>
            )}

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}
          </div>

          {/* AdSense Placeholder 1 (Free tier only) */}
          {isFree }

          {/* Prediction Result=> predictionText*/}
          {prediction &&  (
            <div className="card prediction-result">
              <h2 className="result-title">
                <span className="result-icon">🔮</span>
                Your Prediction
              </h2>
              <div className="prediction-content">
                {/* Top Prediction */}
                <div className="top-prediction-box">
                  <div className="prediction-label">Most Likely Next Event:</div>
                  <h3 className="top-prediction-event">
                    {getEventDescription(prediction.top_prediction.event)}
                  </h3>
                  <div className="confidence-display">
                    <span className="confidence-label">Confidence:</span>
                    <span className="confidence-value">
                      {(prediction.top_prediction.probability * 100).toFixed(2)}%
                    </span>
                  </div>
                  <div className="time-estimate">
                    <span className="time-label">Estimated Time:</span>
                    <span className="time-value">
                      {prediction.estimated_time.years > 0 
                        ? `${prediction.estimated_time.years} Year${prediction.estimated_time.years > 1 ? 's' : ''} and ${prediction.estimated_time.months.toFixed(1)} Months`
                        : prediction.estimated_time.months < 1
                          ? 'Soon (Less than a month!)'
                          : `${prediction.estimated_time.months.toFixed(1)} Months`
                      }
                    </span>
                  </div>
                </div>

                {/* Other Possibilities */}
                <div className="other-predictions">
                  <h4 className="other-title">Other Possibilities:</h4>
                  <ul className="prediction-list">
                    {prediction.top_events.map((item, index) => (
                      <li key={index} className="prediction-item">
                        <span className="event-name">
                          {getEventDescription(item.event)}
                        </span>
                        <span className="event-probability">
                          {(item.probability * 100).toFixed(2)}%
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Right Column - prediction results */}
        <div className="card prediction-result">
        {prediction && predictionText ? (
          <div>
            <h2 className="result-title">
                <span className="result-icon">🔮</span>
                Comprehensive analysis for {user.email.split("@",1)[0] /* + " " + getAge(user.profile.dateOfBirth)*/} from your prediction results within&nbsp; 
                    {prediction.estimated_time.years > 0 
                        ? `${prediction.estimated_time.years} year${prediction.estimated_time.years > 1 ? 's' : ''} and ${prediction.estimated_time.months.toFixed(0)} months`
                        : prediction.estimated_time.months < 1
                          ? 'Soon (Less than a month!)'
                          : `${prediction.estimated_time.months.toFixed(0)} months.`
                      }
                </h2>
               <div class="event-name">{ (predictionText.replaceAll("**","\"")).split('\n\n').map(str => <p class="prediction-par">{str}</p>)}<hr/></div>
             

          </div>):<div class="event-name">
            Comprehensive analysis. Make sure to fill in 
            the date of birth, gender, ethnicity and location 
            in 
            <Link to="/Settings" className="page-link">
                            
                            <span>Settings page ⚙️</span>
                          </Link>
              to get the full report.</div>
        }
        </div>
        
      </div>
    </div>
  );
};

export default PredictionPage;